import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import NewNavbar from "../components/NewNavbar.js";
import Footer from "../components/Footer.js";
import ProductCard from "../components/ProductCard.js";
import SaveToBoardModal from "../components/SaveToBoardModal.js";
import toast from "react-hot-toast";
import { getVisibleCardMatchPercentage } from "../utils.js";
import { usePostMoreProductsSearchMutation } from "../store/api.ts";

const imageHeightClasses = ["h-[310px]", "h-[340px]", "h-[370px]", "h-[400px]"];

const getProductImageHeightClass = (item, index) => {
  const source = String(item?.id ?? item?.name ?? index);
  let hash = 0;

  for (let i = 0; i < source.length; i += 1) {
    hash = source.charCodeAt(i) + ((hash << 5) - hash);
  }

  return imageHeightClasses[Math.abs(hash) % imageHeightClasses.length];
};

const buildMoreProductsSearchCacheKey = ({ query, slugs }) => {
  const normalizedQuery = String(query || "")
    .trim()
    .toLowerCase();

  const normalizedSlugs = [...(slugs || [])]
    .map((slug) => String(slug || "").trim())
    .filter(Boolean)
    .sort()
    .join(",");

  return `${normalizedQuery}::${normalizedSlugs}`;
};

const getProductSlug = (product) => {
  return String(product?.real_slug || product?.slug || "").trim();
};

const normalizeProductForCard = (product) => {
  const matchPercentage =
    product?.matchPercentage ??
    product?.match_percentage ??
    product?.product_score ??
    null;

  const realSlug = getProductSlug(product);
  const productType = realSlug.startsWith("mrk-") ? "marketplace" : "product";

  return {
    ...product,
    id: Number(product?.id),
    name: product?.name || product?.product_name || "Product",
    brand: product?.brand || "",
    price: product?.price || "",
    image: product?.image || product?.image_url || "",
    image_url: product?.image_url || product?.image || "",
    url: product?.url || product?.product_url || "",
    slug: realSlug,
    real_slug: product?.real_slug || realSlug,
    garmentType: product?.garment_type || product?.garmentType || "",
    matchPercentage:
      matchPercentage !== null && matchPercentage !== undefined
        ? Math.round(Number(matchPercentage))
        : null,
    productType,
    product_type: productType,
    is_marketplace: productType === "marketplace",
  };
};

const splitIntoColumns = (items = [], columnCount = 5) => {
  return Array.from({ length: columnCount }, (_, columnIndex) =>
    items.filter((_, itemIndex) => itemIndex % columnCount === columnIndex),
  );
};

const MasonryProducts = ({ items = [], startIndex = 0, onSaveBoard }) => {
  const renderColumns = (columnCount) =>
    splitIntoColumns(items, columnCount).map((columnItems, columnIndex) => (
      <div key={columnIndex} className="flex min-w-0 flex-col gap-3">
        {columnItems.map((item, itemIndex) => {
          const realIndex = startIndex + columnIndex + itemIndex * columnCount;

          return (
            <ProductCard
              key={`${item?.id || item?.name}-${realIndex}`}
              item={item}
              imageHeightClass={getProductImageHeightClass(item, realIndex)}
              matchPercentage={item?.matchPercentage}
              onSaveBoard={onSaveBoard}
              isSavingBoard={false}
              compactActions
            />
          );
        })}
      </div>
    ));

  return (
    <>
      <section className="grid grid-cols-2 gap-3 md:hidden">
        {renderColumns(2)}
      </section>

      <section className="hidden grid-cols-3 gap-3 md:grid xl:hidden">
        {renderColumns(3)}
      </section>

      <section className="hidden grid-cols-5 gap-3 xl:grid">
        {renderColumns(5)}
      </section>
    </>
  );
};

const MarketplaceSearchProductsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [saveBoardProduct, setSaveBoardProduct] = useState(null);
  const [isSaveBoardModalOpen, setIsSaveBoardModalOpen] = useState(false);

  const query = location.state?.query || "";

  const products = useMemo(() => {
    return Array.isArray(location.state?.products)
      ? location.state.products.map(normalizeProductForCard)
      : [];
  }, [location.state?.products]);

  const existingProductSlugs = useMemo(() => {
    return products.map(getProductSlug).filter(Boolean);
  }, [products]);

  const moreProductsRequestSignature = useMemo(() => {
    if (!query.trim()) return "";
    if (!existingProductSlugs.length) return "";

    return buildMoreProductsSearchCacheKey({
      query,
      slugs: existingProductSlugs,
    });
  }, [query, existingProductSlugs]);

  const [
    getMoreProducts,
    {
      data: moreProductsResponse,
      isLoading: isMoreProductsLoading,
      isError: isMoreProductsError,
    },
  ] = usePostMoreProductsSearchMutation({
    fixedCacheKey: moreProductsRequestSignature || "more-products-search-empty",
  });

  const cachedRequestSignature = moreProductsResponse?.requestSignature || "";

  useEffect(() => {
    if (!query.trim()) return;
    if (!existingProductSlugs.length) return;
    if (!moreProductsRequestSignature) return;

    if (cachedRequestSignature === moreProductsRequestSignature) {
      return;
    }

    getMoreProducts({
      moreProductsSearch: {
        query: query.trim(),
        slugs: existingProductSlugs,
      },
    });
  }, [
    query,
    existingProductSlugs,
    moreProductsRequestSignature,
    cachedRequestSignature,
    getMoreProducts,
  ]);

  const moreProducts = useMemo(() => {
    if (!Array.isArray(moreProductsResponse?.products)) return [];

    return moreProductsResponse.products.map(normalizeProductForCard);
  }, [moreProductsResponse]);

  const handleSaveBoard = (item) => {
    if (!item) {
      toast.error("Product details are not available yet.");
      return;
    }
    const itemSlug = item?.real_slug || item?.slug || "";
    if (!itemSlug) {
      toast.error("Product slug is not available yet.");
      return;
    }
    const productType =
      item?.productType ||
      item?.product_type ||
      (item?.is_marketplace === false ? "product" : "marketplace");
    const visibleMatchPercentage = getVisibleCardMatchPercentage(item);
    setSaveBoardProduct({
      ...item,
      slug: itemSlug,
      real_slug: itemSlug,
      productType,
      product_type: productType,
      matchPercentage: visibleMatchPercentage,
      match_percentage: visibleMatchPercentage,
    });
    setIsSaveBoardModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-white font-['Inter','Poppins',sans-serif] text-[#0f172a]">
      <SaveToBoardModal
        isOpen={isSaveBoardModalOpen}
        product={saveBoardProduct}
        onClose={() => {
          setIsSaveBoardModalOpen(false);
          setSaveBoardProduct(null);
        }}
      />
      <NewNavbar />

      <main className="mx-auto max-w-[1480px] px-4 pb-16 pt-6 md:px-5">
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="mb-3 text-[13px] font-medium text-[#666666] hover:text-[#111111]"
            >
              ← Back
            </button>

            <h1 className="m-0 text-[26px] font-semibold text-[#14141F]">
              Product recommendations
            </h1>

            {query ? (
              <p className="mt-1 text-[14px] text-[#73737D]">
                Results for “{query}”
              </p>
            ) : null}
          </div>

          <p className="text-[13px] text-[#73737D]">
            {products.length} products found
          </p>
        </div>

        {products.length > 0 ? (
          <MasonryProducts items={products} onSaveBoard={handleSaveBoard} />
        ) : (
          <div className="rounded-[8px] border border-[#E6E6E6] bg-[#FAFAFA] p-8 text-center">
            <p className="m-0 text-[14px] text-[#73737D]">
              No products found. Please search again.
            </p>
          </div>
        )}

        {products.length > 0 ? (
          <section className="mt-10">
            <div className="mb-4 flex items-center justify-between gap-4">
              <div>
                <h2 className="m-0 text-[22px] font-semibold text-[#14141F]">
                  More products
                </h2>
                <p className="mt-1 text-[13px] text-[#73737D]">
                  More recommendations based on your search.
                </p>
              </div>

              {moreProductsResponse?.count ? (
                <p className="text-[13px] text-[#73737D]">
                  {moreProductsResponse.count} more found
                </p>
              ) : null}
            </div>

            {isMoreProductsLoading ? (
              <section className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
                {Array.from({ length: 10 }).map((_, index) => (
                  <div
                    key={index}
                    className={[
                      "animate-pulse rounded-[8px] bg-[#EEF1F4]",
                      index % 4 === 0
                        ? "h-[310px]"
                        : index % 4 === 1
                          ? "h-[340px]"
                          : index % 4 === 2
                            ? "h-[370px]"
                            : "h-[400px]",
                    ].join(" ")}
                  />
                ))}
              </section>
            ) : isMoreProductsError ? (
              <div className="rounded-[8px] border border-red-100 bg-red-50 p-4">
                <p className="m-0 text-[14px] text-red-600">
                  Failed to load more products.
                </p>
              </div>
            ) : moreProducts.length > 0 ? (
              <MasonryProducts
                items={moreProducts}
                startIndex={products.length}
                onSaveBoard={handleSaveBoard}
              />
            ) : (
              <div className="rounded-[8px] border border-[#E6E6E6] bg-[#FAFAFA] p-6 text-center">
                <p className="m-0 text-[14px] text-[#73737D]">
                  No more products found.
                </p>
              </div>
            )}
          </section>
        ) : null}
      </main>
      <Footer />
    </div>
  );
};

export default MarketplaceSearchProductsPage;
